/******************************************************************************
 * Droits d'auteur (c) 2024 Niralakka
 *
 * La permission est accordée, gratuitement, à toute personne obtenant une copie de ce logiciel
 * et des fichiers de documentation associés (le "Logiciel"), de traiter le Logiciel sans restriction,
 * y compris, sans limitation, les droits d'utiliser, de copier, de modifier, de fusionner, de publier, de distribuer, de concéder
 * une sous-licence, et/ou de vendre des copies du Logiciel, et de permettre aux personnes à qui le Logiciel est fourni de le faire,
 * sous réserve des conditions suivantes :
 *
 * L'avis de droit d'auteur ci-dessus et cet avis d'autorisation doivent être inclus dans toutes les copies ou parties substantielles
 * du Logiciel.
 *
 * LE LOGICIEL EST FOURNI "TEL QUEL", SANS GARANTIE D'AUCUNE SORTE, EXPLICITE OU IMPLICITE,
 * Y COMPRIS, MAIS SANS S'Y LIMITER, LES GARANTIES DE QUALITÉ MARCHANDE, D'ADÉQUATION À UN USAGE PARTICULIER ET D'ABSENCE DE CONTREFAÇON.
 * EN AUCUN CAS LES AUTEURS OU LES TITULAIRES DES DROITS D'AUTEUR NE SERONT RESPONSABLES DE TOUTE RÉCLAMATION, DOMMAGES OU AUTRE RESPONSABILITÉ,
 * QUE CE SOIT DANS UNE ACTION DE CONTRAT, DE DÉLIT OU AUTRE, DÉCOULANT DE, HORS DE OU EN LIEN AVEC LE LOGICIEL OU L'UTILISATION
 * OU D'AUTRES TRANSACTIONS DANS LE LOGICIEL.
 *
 ******************************************************************************/

// Version: 2.0.0

import { NrlkAPI } from "./api.js";

/**
 * Classe modèle pour les appels HTTP relatifs aux tickets (API V2).
 *
 * @param {string} entreprise - Slug de l'entreprise (header `Entreprise`)
 * @param {string|null} token - Jeton d'authentification (header `Token`)
 */
export class NrlkTicket {
  constructor(entreprise, token = null) {
    this.api = new NrlkAPI(entreprise, token);
  }

  /**
   * Définit l'entreprise dans les headers de l'API.
   *
   * @param {string} entreprise
   */
  setEntreprise(entreprise) { this.api.setEntreprise(entreprise); }

  /**
   * Définit le token dans les headers de l'API.
   *
   * @param {string} token
   */
  setToken(token) { this.api.setToken(token); }

  /**
   * Récupère un ticket par son identifiant.
   *
   * @param {number} idTicket - Identifiant du ticket
   * @returns {Promise<object>} Réponse brute de l'API
   */
  async getTicket(idTicket) {
    return this.api.get(`/ticket/${idTicket}`);
  }

  // Filtre via requete — valeurs statut à vérifier selon la config API V2
  /**
   * Récupère tous les tickets dont le statut est "ouvert".
   *
   * @returns {Promise<object>} Réponse brute de l'API
   */
  async getTicketsOuverts() {
    return this.api.get("/ticket/", { requete: "statut:'ouvert'" });
  }

  /**
   * Récupère tous les tickets dont le statut est "ferme".
   *
   * @returns {Promise<object>} Réponse brute de l'API
   */
  async getTicketsFermes() {
    return this.api.get("/ticket/", { requete: "statut:'ferme'" });
  }

  /**
   * Récupère les tickets ouverts d'un client.
   *
   * @param {number} idClient - Identifiant du client
   * @returns {Promise<object>} Réponse brute de l'API
   */
  async getClientTicketsOuverts(idClient) {
    return this.api.get(`/client/${idClient}/ticket`, { requete: "statut:'ouvert'" });
  }

  /**
   * Récupère les tickets fermés d'un client.
   *
   * @param {number} idClient - Identifiant du client
   * @returns {Promise<object>} Réponse brute de l'API
   */
  async getClientTicketsFermes(idClient) {
    return this.api.get(`/client/${idClient}/ticket`, { requete: "statut:'ferme'" });
  }

  /**
   * Crée un nouveau ticket.
   *
   * @param {object} ticket - Corps de la requête `{ ticket: { ... } }`
   * @returns {Promise<object>} Réponse brute de l'API
   */
  async createTicket(ticket) {
    return this.api.post("/ticket/", ticket);
  }

  /**
   * Ajoute une intervention à un ticket existant.
   *
   * @param {number} idTicket - Identifiant du ticket
   * @param {object} interventions - Corps de la requête `{ intervention: { ... } }`
   * @returns {Promise<object>} Réponse brute de l'API
   */
  async addInterventionsTicket(idTicket, interventions) {
    return this.api.post(`/ticket/${idTicket}/intervention`, interventions);
  }

  /**
   * Réouvre un ticket fermé.
   *
   * @param {number} idTicket - Identifiant du ticket
   * @returns {Promise<object>} Réponse brute de l'API
   */
  async ouvrirTicket(idTicket) {
    return this.api.put(`/ticket/${idTicket}/reouvrir`);
  }

  /**
   * Clôture un ticket en définissant sa méthode de facturation.
   *
   * @param {number} idTicket - Identifiant du ticket
   * @param {object} ticket - Corps de la requête `{ ticket: { methode_facturation: ... } }`
   * @returns {Promise<object>} Réponse brute de l'API
   */
  async facturationTicket(idTicket, ticket) {
    return this.api.put(`/ticket/${idTicket}/fermer`, ticket);
  }

  /**
   * Met à jour les informations d'un ticket.
   *
   * @param {number} idTicket - Identifiant du ticket
   * @param {object} ticket - Corps de la requête `{ ticket: { ... } }`
   * @returns {Promise<object>} Réponse brute de l'API
   */
  async updateTicket(idTicket, ticket) {
    return this.api.put(`/ticket/${idTicket}`, ticket);
  }

  /**
   * Ajoute un ou plusieurs fichiers à un ticket (multipart/form-data).
   *
   * @param {number} idTicket - Identifiant du ticket
   * @param {FormData} fichiers - FormData contenant le champ `fichier`
   * @returns {Promise<object>} Réponse brute de l'API
   */
  async addFichiersTicket(idTicket, fichiers) {
    return this.api.post(`/ticket/${idTicket}/fichier`, fichiers);
  }
}
